import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";
import TriangleAlert from "lucide-react/dist/esm/icons/triangle-alert";
import type { WorkerRow } from "@/lib/workers/types";

/**
 * A worker whose row was renamed after it was provisioned.
 *
 * The load balancer routes the row's hostname while the container still mints
 * its browser URLs against the name it was created with, so every reconnect,
 * VNC and DevTools link this worker hands out points at a path the LB answers
 * "unknown worker" to. Nothing else on the page looks wrong — the container is
 * up, healthy and in the pool — which is exactly why this has to be stated at
 * rest rather than left to a hover.
 */
export function RouteDriftNote({ worker }: { worker: WorkerRow }) {
	const drift = worker.routeDrift;
	if (!drift) return null;
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="mt-0.5 flex cursor-default items-center gap-1 text-[0.6875rem] text-[var(--status-warning)]">
					<TriangleAlert className="size-3 shrink-0" aria-hidden="true" />
					<span className="truncate">
						answers as <span className="font-mono">{drift.answersAs}</span>
					</span>
				</span>
			</TooltipTrigger>
			<TooltipContent side="bottom" className="max-w-xs">
				<span className="font-medium">Route mismatch</span>
				<span className="mt-1 block text-muted-foreground">
					This worker runs in container{" "}
					<span className="font-mono">{drift.actualContainerName}</span> and
					still identifies itself as{" "}
					<span className="font-mono">{drift.answersAs}</span>, so the URLs it
					mints point at{" "}
					<span className="font-mono">/w/{drift.answersAs}/</span> while the
					load balancer only routes{" "}
					<span className="font-mono">/w/{worker.hostname}/</span>. Reconnect,
					VNC and DevTools links from this worker will not resolve. Deploy a
					replacement under the name you want and remove this one.
				</span>
			</TooltipContent>
		</Tooltip>
	);
}
